import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import moment from 'moment';
import {
  FiBell, FiCpu, FiEdit3, FiGitBranch, FiMail, FiTrendingUp, FiTarget, FiZap, FiCalendar, FiCheckSquare, FiFilm, FiHome, FiLogOut, FiMessageSquare,
  FiActivity, FiChevronDown, FiLock, FiTerminal, FiBookOpen, FiMenu, FiMoon, FiShield, FiPlus, FiSearch, FiSidebar, FiSun, FiUser, FiUsers, FiGrid, FiX,
} from 'react-icons/fi';
import { ChatState } from '../Context/ChatProvider';
import Radio from './Radio';
import { Avatar, Button, Popover, ToastProvider, useTheme } from '../ui';
import { WorkspaceProvider, notifText, useWorkspace } from './Workspace';
import { DEFAULT_COLUMNS } from '../lib/tasks';
import CommandPalette from './CommandPalette';
import LanguageMenu from '../i18n/LanguageMenu';
import IncomingCall from './IncomingCall';
import { AssistantProvider } from '../Robot/assistantStore';
import AssistantFab from '../Robot/AssistantFab';
import { GiaNavIcon } from '../Robot/Gia';
import logoFull from '../assets/gq_full.png';
import logoIcon from '../assets/gq_icon.png';
import { getRecent, getStarred } from '../Projects/recent';
import './shell.css';
import { t } from '../i18n';

// screen: the key of the screen in the permission catalogue (server Backend/permissions/catalog.js); hidden when the person's role gives no access
const NAV = [
  { section: t("Workspace") },
  { to: '/dashboard', label: t("Dashboard"), icon: FiHome, screen: 'dashboard' },
  { to: '/scheduler', label: t("Calendar"), icon: FiCalendar, screen: 'calendar' },
  { to: '/chat', label: t("Chat"), icon: FiMessageSquare, screen: 'chat' },
  { to: '/notes', label: t("Notes"), icon: FiEdit3, screen: 'notes' },
  { to: '/performance', label: t("Performance"), icon: FiTrendingUp, internal: true },
  { to: '/office', label: t("Virtual Office"), icon: FiGrid, screen: 'office' },
  { to: '/library', label: t("GQ Library"), icon: FiBookOpen, screen: 'library' },
  { section: t("Administration") },
  { to: '/userManagement', label: t("Workers"), icon: FiUsers, screen: 'workers' },
  { to: '/organization', label: t("Organization"), icon: FiGitBranch, screen: 'hr' },
  { to: '/admin/permissions', label: t("Permissions"), icon: FiLock, screen: 'permissions' },
  { to: '/admin/email', label: t("Email"), icon: FiMail, screen: 'email' },
  { to: '/admin/audit', label: t("Audit"), icon: FiActivity, screen: 'audit' },
  { to: '/admin/ai-usage', label: t("AI usage"), icon: FiCpu, screen: 'aiusage' },
  { to: '/access', label: t("Access control"), icon: FiShield, screen: 'access' },
  { section: t("Work") },
  { to: '/projects', label: t("Projects"), icon: FiCheckSquare, screen: 'projects' },
  { to: '/apiclient', label: t("API Client"), icon: FiTerminal, screen: 'apiclient' },
  { to: '/autogen', label: t("Auto Generate"), icon: FiZap, screen: 'autogen' },
  { to: '/robot', label: t("QIRA"), icon: GiaNavIcon, screen: 'assistant' },
  { section: t("Marketing") },
  { to: '/leads', label: t("Leads"), icon: FiTarget, screen: 'leads' },
  { section: t("Discover") },
  { to: '/cinema', label: t("Cinema"), icon: FiFilm, screen: 'cinema' },
];

function ProjectLinks() {
  const { projects } = useWorkspace();
  const [, force] = useState(0);
  useEffect(() => {
    const on = () => force((n) => n + 1);
    window.addEventListener('gq-projects-changed', on);
    return () => window.removeEventListener('gq-projects-changed', on);
  }, []);
  const starred = getStarred();
  const recent = getRecent();
  const ids = [...new Set([...starred, ...recent])].slice(0, 5);
  const list = ids.map((id) => projects.find((p) => p._id === id)).filter(Boolean);
  return (
    <div className="nav-sub">
      {list.map((p) => (
        <NavLink key={p._id} to={`/projects/${p.key}/board`} className={({ isActive }) => `nav-item sub ${isActive ? 'active' : ''}`} title={p.name}>
          <span className="nav-emoji">{p.logo ? <img src={p.logo} alt="" loading="lazy" /> : p.icon}</span>
          <span className="nav-label">{p.name}</span>
        </NavLink>
      ))}
    </div>
  );
}

function Sidebar({ collapsed, onToggle, open, onClose }) {
  const { me, notifs, can, levels } = useWorkspace();
  const unreadChat = notifs.filter((n) => !n.read && n._id).length;
  // admin-only entries need the permissions to have loaded (they are never shown optimistically)
  // the permissions screen is gated by the live value from the server (can('permissions')), not by the isAdmin
  // stored at login, so a promotion applies at once without signing out and in again
  const visible = NAV.filter((n) => !(n.internal && me && me.isExternal)).filter((n) => (n.screen === 'permissions' ? !!(levels && can('permissions')) : n.screen ? can(n.screen) && (levels ? levels[n.screen] !== 'none' : true) : true))
    .filter((n, i, all) => !n.section || (all[i + 1] && !all[i + 1].section)); // no empty section titles
  // the list of project shortcuts under "Projects" can be folded; the choice is remembered
  const [projOpen, setProjOpen] = useState(() => { try { return localStorage.getItem('gq-nav-projects') !== '0'; } catch (e) { return true; } });
  const toggleProjects = (e) => {
    e.preventDefault(); e.stopPropagation();
    setProjOpen((o) => { try { localStorage.setItem('gq-nav-projects', o ? '0' : '1'); } catch (x) { /* ignore */ } return !o; });
  };
  const logout = () => {
    ['userInfo', 'selectedChat', 'chato_user', 'GPT_data'].forEach((k) => localStorage.removeItem(k));
    window.location.replace('/');
  };
  return (
    <aside className={`sidebar ${open ? 'open' : ''}`} aria-label={t("Main navigation")}>
      <div className="brand">
        <img className="brand-full" src={logoFull} alt={t("Good Quality Management")} />
        <img className="brand-mini" src={logoIcon} alt={t("Good Quality Management")} />
        <button className="icon-btn drawer-close" onClick={onClose} aria-label={t("Close menu")} title={t("Close menu")}><FiX /></button>
      </div>
      <nav className="side-scroll" aria-label={t("Main navigation")}>
      {visible.map((n, i) =>
        n.section ? (
          <div className="nav-section" key={i}>{n.section}</div>
        ) : (
          <React.Fragment key={n.to}>
            <NavLink to={n.to} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} title={n.label}>
              <n.icon />
              <span className="nav-label">{n.label}</span>
              {n.to === '/chat' && unreadChat > 0 && <span className="count">{unreadChat}</span>}
              {n.to === '/projects' && (
                <button type="button" className={`nav-fold ${projOpen ? 'open' : ''}`} onClick={toggleProjects} aria-expanded={projOpen} aria-label={projOpen ? t("Hide projects") : t("Show projects")} title={projOpen ? t("Hide projects") : t("Show projects")}>
                  <FiChevronDown />
                </button>
              )}
            </NavLink>
            {n.to === '/projects' && projOpen && <ProjectLinks />}
          </React.Fragment>
        )
      )}
      </nav>
      <div className="sidebar-foot">
        <button className="nav-item" style={{ width: '100%', border: 0, background: 'transparent', cursor: 'pointer' }} onClick={onToggle} title={t("Collapse sidebar")}>
          <FiSidebar />
          <span className="nav-label">{collapsed ? t("Expand") : t("Collapse")}</span>
        </button>
        <Popover up trigger={
          <button className="side-user" title={me?.fullname}>
            <Avatar name={me?.fullname} src={me?.img_user} size={32} presence="online" />
            <span className="side-user-info">
              <b>{me?.fullname}</b>
              <span>{me?.role}</span>
            </span>
          </button>
        }>
          <NavLink to="/profile" className="menu-item"><FiUser /> {t("Profile")}</NavLink>
          <div className="menu-sep" />
          <button className="menu-item danger" onClick={logout}><FiLogOut /> {t("Log out")}</button>
        </Popover>
      </div>
    </aside>
  );
}

function NotificationsPanel() {
  const { me, notifs, unread, markRead, markAllRead, clearNotifs } = useWorkspace();
  const navigate = useNavigate();
  const [tab, setTab] = useState('unread');
  const list = notifs.map((n, i) => ({ n, i })).filter(({ n }) => (tab === 'unread' ? !n.read : n.read));
  return (
    <Popover align="right" trigger={
      <button className="icon-btn" title={t("Notifications")}>
        <FiBell />
        {unread > 0 && <span className="badge">{unread > 99 ? '99+' : unread}</span>}
      </button>
    }>
      {(close) => (
        <div className="notif-panel">
          <div className="notif-head">
            {t("Notifications")}
            <span>
              <Button size="sm" variant="subtle" onClick={markAllRead}>{t("Mark all read")}</Button>
              <Button size="sm" variant="subtle" onClick={clearNotifs}>{t("Clear")}</Button>
            </span>
          </div>
          <div className="tabs" style={{ padding: '0 8px' }}>
            <button className={`tab ${tab === 'unread' ? 'active' : ''}`} onClick={() => setTab('unread')}>{t("Unread")}</button>
            <button className={`tab ${tab === 'read' ? 'active' : ''}`} onClick={() => setTab('read')}>{t("Read")}</button>
          </div>
          <div className="notif-list">
            {list.length === 0 && <div className="empty"><h4>{t("You're all caught up")}</h4>{t("No")} {tab} {t("notifications.")}</div>}
            {list.map(({ n, i }) => (
              <div
                key={i}
                className={`notif-item ${n.read ? '' : 'unread'}`}
                onClick={() => {
                  markRead(i);
                  close();
                  const m = !n._id && /^([A-Z][A-Z0-9]+)-(\d+)/.exec(n.content || '');
                  navigate(n.kind === 'access' ? '/access' : n._id ? '/chat' : m ? `/projects/${m[1]}/board?selectedIssue=${m[1]}-${m[2]}` : n.taskNum ? `/tasks?task=${n.taskNum}` : '/projects');
                }}
              >
                <Avatar name={notifText(n).name} src={n.sender?.img_user} size={32} />
                <div className="body">
                  {n.kind === 'access'
                    ? <div><b>{notifText(n).headline}</b></div>
                    : <div><b>{n.sender?.fullname}</b> {n._id ? ((n.mentions || []).includes(me._id) ? t("mentioned you") : t("sent you a message")) : t("updated an issue")}</div>}
                  <div className="content">{n.kind === 'access' ? notifText(n).body : notifText(n).headline}</div>
                  <div className="meta">
                    {moment(n.updatedAt).fromNow()}
                    {!n._id && n.taskNum ? t("· TASK-{{taskNum}}", { taskNum: n.taskNum }) : ''}
                    {!n._id && n.status ? ` · ${(DEFAULT_COLUMNS.find((c) => c.key === n.status) ? t(DEFAULT_COLUMNS.find((c) => c.key === n.status).name) : n.status)}` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Popover>
  );
}

function Topbar({ onMenu }) {
  const { setPaletteOpen } = useWorkspace();
  const { address } = ChatState();
  const navigate = useNavigate();
  const [theme, toggleTheme] = useTheme();
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    axios.get(`${address}/api/weather`).then((r) => setWeather(r.data)).catch(() => {});
  }, [address]);
  const isMac = /Mac/i.test(navigator.platform);
  return (
    <header className="topbar">
      <button className="icon-btn menu-btn" onClick={onMenu} aria-label={t("Open menu")} title={t("Open menu")}><FiMenu /></button>
      <button className="search-trigger" onClick={() => setPaletteOpen(true)} aria-label={t("Search")}>
        <FiSearch /> <span className="st-text">{t("Search tasks, people, pages…")}</span>
        <span className="kbd">{isMac ? '⌘' : t("Ctrl")} K</span>
      </button>
      <Button variant="primary" className="create-btn" onClick={() => navigate('/tasks?create=1')} aria-label={t("Create")}><FiPlus /> <span className="create-label">{t("Create")}</span></Button>
      <span className="spacer" />
      {weather?.current && (
        <span className="weather" title={`${weather.location?.name || ''}${weather.current.condition?.text ? ` · ${t(weather.current.condition.text)}` : ''}${weather.source ? ` · ${weather.source}` : ''}`}>
          {weather.current.condition?.emoji ? <span className="weather-emoji" aria-hidden="true">{weather.current.condition.emoji}</span> : weather.current.condition?.icon && <img src={weather.current.condition.icon} alt="" />}
          {Math.round(weather.current.temp_c)}° · {moment().format('ddd, D MMM')}
        </span>
      )}
      <Radio />
      <LanguageMenu />
      <button className="icon-btn" onClick={toggleTheme} title={t("Toggle dark mode")}>
        {theme === 'dark' ? <FiSun /> : <FiMoon />}
      </button>
      <NotificationsPanel />
    </header>
  );
}

const TABS = [
  { to: '/dashboard', label: t("Home"), icon: FiHome, screen: 'dashboard' },
  { to: '/projects', label: t("Projects"), icon: FiCheckSquare, screen: 'projects' },
  { to: '/chat', label: t("Chat"), icon: FiMessageSquare, screen: 'chat' },
  { to: '/scheduler', label: t("Calendar"), icon: FiCalendar, screen: 'calendar' },
  { to: '/office', label: t("Office"), icon: FiGrid, screen: 'office' },
];

// Phone navigation: the five everyday places one tap away, the rest in the drawer
function BottomNav({ onMore }) {
  const { notifs, can } = useWorkspace();
  const unreadChat = notifs.filter((n) => !n.read && n._id).length;
  return (
    <nav className="bottom-nav" aria-label={t("Primary")}>
      {TABS.filter((x) => can(x.screen)).map((t) => (
        <NavLink key={t.to} to={t.to} className={({ isActive }) => `bn-item ${isActive ? 'active' : ''}`} title={t.label}>
          <span className="bn-icon"><t.icon />{t.to === '/chat' && unreadChat > 0 && <i className="bn-dot">{unreadChat}</i>}</span>
          <span>{t.label}</span>
        </NavLink>
      ))}
      <button className="bn-item" onClick={onMore} aria-label={t("More")} title={t("More")}>
        <span className="bn-icon"><FiMenu /></span>
        <span>{t("More")}</span>
      </button>
    </nav>
  );
}

function ShellInner() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('gq-side') === '1');
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { eventAlert, setEventAlert, incomingCall, paletteOpen } = useWorkspace();
  useEffect(() => {
    try {
      localStorage.setItem('gq-side', collapsed ? '1' : '0');
    } catch (e) {
      /* ignore */
    }
  }, [collapsed]);
  const [drawer, setDrawer] = useState(false);
  useEffect(() => setDrawer(false), [pathname]); // close the menu after navigating
  const flush = pathname === '/office' || pathname === '/chat' || pathname === '/apiclient';
  return (
    <div className={`shell ${collapsed ? 'collapsed' : ''}`}>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} open={drawer} onClose={() => setDrawer(false)} />
      {drawer && <div className="drawer-scrim" onClick={() => setDrawer(false)} />}
      <Topbar onMenu={() => setDrawer(true)} />
      <main className={`main ${flush ? 'flush' : ''}`}>
        <Outlet />
      </main>
      <BottomNav onMore={() => setDrawer(true)} />
      <AssistantFab />
      {paletteOpen && <CommandPalette />}
      {incomingCall && <IncomingCall />}
      {eventAlert && (
        <div className="event-banner">
          <FiCalendar />
          <div>
            <b>{eventAlert.title}</b> {t("starts in")} {eventAlert.time} {t("min")}
            {eventAlert.location ? ` · ${eventAlert.location}` : ''}
          </div>
          {eventAlert.meetingLink && <Button size="sm" variant="primary" onClick={() => { navigate(eventAlert.meetingLink, { state: { title: eventAlert.title } }); setEventAlert(null); }}>{t("Join")}</Button>}
          <Button size="sm" variant="subtle" onClick={() => setEventAlert(null)}>{t("Dismiss")}</Button>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  return (
    <ToastProvider>
      <WorkspaceProvider>
        <AssistantProvider>
          <ShellInner />
        </AssistantProvider>
      </WorkspaceProvider>
    </ToastProvider>
  );
}
